import { IFundingRepository, FundingFilter, FundingExist } from '../domain/repositories';
import { FundingEntity } from '../domain/entities';
import { ProductId, ProductMetaCode } from '@services/MarppleShop/features/ProductMeta';
import { FundingMeta } from '@services/MarppleShop/features/ProductMeta/domain/value-objects';
import { AllMpsPoolType, AllPoolType } from '@services/MarppleShop/shared/database/db.ts';
import { isEmpty, pipe, map, toArray } from '@fxts/core';
import { FundingCreator } from '@services/MarppleShop/features/Funding/domain/value-objects/funding-creator.ts';
import { Localized } from '../../../../../shared/type';
import { FundingCompositeSortStrategy } from '@services/MarppleShop/features/Funding';
import { FundingDetails } from '@services/MarppleShop/features/Funding/domain/value-objects/funding-details.ts';
import { transformers } from '@services/MarppleShop/features/ProductList/application/transformers';

interface FundingDbtRow {
  product_id: number;
  store_id: number;
  sell_start_at?: Date;
  sell_end_at?: Date;
  name_intl: Localized<string>;
  description?: string;

  /**
   * 썸네일 그리는데 필요한 데이터
   * 비정형 데이터라서 unknown 으로 표기
   * @see {ProductRow}
   */
  thumbnails: unknown;
  thumbnail_ratio: number | null;
  adult_check_needed: boolean;

  goal_amount: string;
  quantity: number;
  order_count: number;
  can_selling: boolean;
  domain_name: string;
  store_name: string;
  is_force_out_of_stock: boolean;
  is_public: boolean;
  is_quantity_public: boolean;
  out_of_stock: boolean;
  created_at: Date;
  updated_at: Date;
}

export class FundingRepository implements IFundingRepository {
  private readonly DEFAULT_LIMIT = 15;
  private readonly DEFAULT_OFFSET = 0;

  constructor(
    private readonly db: AllPoolType,
    private readonly dbt: AllMpsPoolType,
  ) {}

  async findByProductId(productId: ProductId): Promise<FundingEntity | undefined> {
    const row = await this.dbt.QUERY1<FundingDbtRow>`
      SELECT
        p.id as product_id,
        p.name_intl,
        p.sell_start_at,
        p.sell_end_at,
        p.thumbnails,
        p.thumbnail_ratio::float,
        p.quantity,
        p.order_count,
        p.can_selling,
        p.domain_name,
        p.store_name,
        p.store_id,
        p.is_force_out_of_stock,
        p.is_quantity_public,
        p.is_public,
        p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1) AS out_of_stock,
        mpmcv.value_numeric as goal_amount,
        mpmcv.value_text as description,
        mpmcv.created_at,
        mpmcv.updated_at
      FROM products_dbt AS p
             INNER JOIN mps_product_meta_code_variants_view AS mpmcv ON mpmcv.product_id = p.id
             INNER JOIN meta_code_variants_view AS mcv ON mcv.id = mpmcv.meta_code_variant_id
             INNER JOIN meta_codes_view AS mc ON mc.code = mcv.meta_code AND mc.marketplace_id = mcv.meta_code_marketplace_id
             LEFT OUTER JOIN LATERAL (
               SELECT SUM(quantity) AS sum_of_quantity 
               FROM spo_item_stocks_view
               WHERE deleted_at IS NULL
                 AND spo_id = p.spo_id
               ) spo_sum ON TRUE
      WHERE p.id = ${productId.value}
        AND mcv.meta_code = ${ProductMetaCode.FUNDING}
        AND mcv.status = 'ACTIVE'
        AND mc.status = 'ACTIVE'
    `;

    if (!row) return undefined;

    return this.mapToEntity(row);
  }

  async findAll(filter: FundingFilter): Promise<FundingEntity[]> {
    const {
      storeIds = [],
      productIds = [],
      limit = this.DEFAULT_LIMIT,
      offset = this.DEFAULT_OFFSET,
    } = filter;

    const rows = await this.dbt.QUERY<FundingDbtRow>`
      SELECT
        p.id as product_id,
        p.name_intl,
        p.sell_start_at,
        p.sell_end_at,
        p.thumbnails,
        p.thumbnail_ratio::float,
        p.quantity,
        p.order_count,
        p.can_selling,
        p.domain_name,
        p.store_name,
        p.store_id,
        p.is_force_out_of_stock,
        p.is_quantity_public,
        p.is_public,
        p.adult_check_needed,
        p.is_force_out_of_stock OR (COALESCE(spo_sum.sum_of_quantity, 1) < 1) AS out_of_stock,
        mpmcv.value_numeric as goal_amount,
        mpmcv.value_text as description,
        mpmcv.created_at,
        mpmcv.updated_at
      FROM products_dbt AS p
             INNER JOIN mps_product_meta_code_variants_view AS mpmcv ON mpmcv.product_id = p.id
             INNER JOIN meta_code_variants_view AS mcv ON mcv.id = mpmcv.meta_code_variant_id
             INNER JOIN meta_codes_view AS mc ON mc.code = mcv.meta_code AND mc.marketplace_id = mcv.meta_code_marketplace_id
             LEFT OUTER JOIN LATERAL (
               SELECT SUM(quantity) AS sum_of_quantity
               FROM spo_item_stocks_view
               WHERE deleted_at IS NULL
                 AND spo_id = p.spo_id
               ) spo_sum ON TRUE
      WHERE 1=1
        AND ${this.dbt.CL(`p.is_use_creator${_en}`)}
        AND ${this.dbt.CL(`p.price${_en}`)} IS NOT NULL
        AND ${this.dbt.CL(`p.public_at`)} < NOW()
        AND mcv.meta_code = ${ProductMetaCode.FUNDING}
        AND mcv.status = 'ACTIVE'
        AND mc.status = 'ACTIVE'
        AND p.is_public = TRUE
        ${isEmpty(storeIds) ? this.dbt.SQL`` : this.dbt.SQL`AND ${this.db.IN('p.store_id', storeIds)}`}
        ${
          isEmpty(productIds)
            ? this.dbt.SQL``
            : this.dbt.SQL`AND ${this.db.IN(
                'p.id',
                pipe(
                  productIds,
                  map((productId) => productId.value),
                  toArray,
                ),
              )} `
        }
      ${filter.strategies ? FundingCompositeSortStrategy.create(this.db, filter.strategies, filter.sort).build() : this.db.SQL``}
      OFFSET ${offset}
      LIMIT ${limit}
    `;

    return pipe(
      rows,
      map((row) => this.mapToEntity(row)),
      toArray,
    );
  }

  async countAll(filter: FundingFilter): Promise<number> {
    const { storeIds = [], productIds = [] } = filter;

    const row = await this.dbt.QUERY1<{ total_count: number }>`
    SELECT COUNT(*) as total_count
    FROM products_dbt AS p
           INNER JOIN mps_product_meta_code_variants_view AS mpmcv ON mpmcv.product_id = p.id
           INNER JOIN meta_code_variants_view AS mcv ON mcv.id = mpmcv.meta_code_variant_id
           INNER JOIN meta_codes_view AS mc ON mc.code = mcv.meta_code AND mc.marketplace_id = mcv.meta_code_marketplace_id
    WHERE 1=1
      AND ${this.dbt.CL(`p.is_use_creator${_en}`)}
      AND ${this.dbt.CL(`p.price${_en}`)} IS NOT NULL
      AND ${this.dbt.CL(`p.public_at`)} < NOW()
      AND mcv.meta_code = ${ProductMetaCode.FUNDING}
      AND mcv.status = 'ACTIVE'
      AND mc.status = 'ACTIVE'
      AND p.is_public = TRUE
      ${isEmpty(storeIds) ? this.dbt.SQL`` : this.dbt.SQL`AND ${this.db.IN('store_id', storeIds)}`}
      ${
        isEmpty(productIds)
          ? this.dbt.SQL``
          : this.dbt.SQL`AND ${this.db.IN(
              'product_id',
              pipe(
                productIds,
                map((productId) => productId.value),
                toArray,
              ),
            )} `
      }
  `;

    return Number(row?.total_count || 0);
  }

  async exist(options: FundingExist): Promise<boolean> {
    const { storeId, productIds } = options;

    const row = await this.dbt.QUERY1<{ exist: boolean }>`
      SELECT EXISTS (
        SELECT 1
        FROM products_dbt AS p
          INNER JOIN mps_product_meta_code_variants_view AS mpmcv ON mpmcv.product_id = p.id
          INNER JOIN meta_code_variants_view AS mcv ON mcv.id = mpmcv.meta_code_variant_id
          INNER JOIN meta_codes_view AS mc ON mc.code = mcv.meta_code AND mc.marketplace_id = mcv.meta_code_marketplace_id
        WHERE 1=1
          AND mcv.meta_code = ${ProductMetaCode.FUNDING}
          AND mcv.status = 'ACTIVE'
          AND mc.status = 'ACTIVE'
          AND p.is_public = TRUE
          ${storeId ? this.dbt.SQL`AND p.store_id = ${storeId}` : this.dbt.SQL``}
          ${
            productIds
              ? this.dbt.SQL`AND ${this.db.IN(
                  'p.id',
                  pipe(
                    productIds,
                    map((productId) => productId.value),
                    toArray,
                  ),
                )}`
              : this.dbt.SQL``
          }
        ) as exist
    `;

    return Boolean(row?.exist);
  }

  private mapToEntity(row: FundingDbtRow): FundingEntity {
    const productId = new ProductId(row.product_id);
    const thumbnail = transformers.thumbnail(row);
    const fundingMeta = new FundingMeta({
      target_amount: parseInt(row.goal_amount),
      description: row.description,
    });
    const fundingCreator = FundingCreator.of(row.store_id, productId, row.store_name, row.domain_name);

    const fundingDetails = FundingDetails.of({
      name: row.name_intl,
      thumbnailInfo: thumbnail,
      totalQuantity: row.quantity,
      soldQuantity: row.order_count,
      canSelling: row.can_selling,
      isPublic: row.is_public,
      isForceOutOfStock: row.is_force_out_of_stock,
      isQuantityPublic: row.is_quantity_public,
      outOfStock: row.out_of_stock,
      start: row.sell_start_at ? new Date(row.sell_start_at) : undefined,
      end: row.sell_end_at ? new Date(row.sell_end_at) : undefined,
    });

    return new FundingEntity({
      productId,
      fundingMeta,
      fundingCreator,
      fundingDetails,
      createdAt: new Date(row.created_at),
      updatedAt: new Date(row.updated_at),
    });
  }
}
